/**
 * cart-promo-cascade.js
 *
 * 카트 cascade + confirm modal 모듈 (Phase J, 2026-05-27).
 *
 * 책임:
 *   1. parent X 클릭 → BXGY 자식 라인 cascade 제거 (handleParentRemove → confirm → parentCascade)
 *   2. cart-confirm-modal 의 open/close/OK dispatch (clear-all / remove-bxgy-promo / remove-parent-cascade 3 action)
 *   3. BXGY 해제 (removeBxgyPromo)
 *   4. clear all cart
 *
 * 상태 모델:
 *   - confirm modal context 는 모듈 local state (_cartConfirm) — confirm modal 이 DOM 단일이라 singleton 안전.
 *   - hub instance 의 다른 함수 (refreshCartSection / applyToCart / promotionCellsToClear / clearGiftRows / refreshDropdownInput) 는 hub 매개변수로 받음.
 *
 * 로드 순서:
 *   1. cart-coupon-utils.js
 *   2. cart-promo-effects.js
 *   3. cart-promo-storefront.js
 *   4. cart-promo-bxgy.js
 *   5. cart-promo-cascade.js  ← 본 파일
 *   6. cart-coupon-hub.js
 *
 * 회귀 박제:
 *   - cart-remove-button capture phase 차단 (2026-05-26) — wiki/docs/bxgy-reentry-cleanup-and-cart-remove-button-capture-2026-05-26.md
 *   - drawer fallback data-gift-parent (2026-05-27)
 *   - sections 기반 DOM swap (drawer + main-cart 동시 갱신)
 */
(function (global) {
  'use strict';

  // 모듈 local state — cart-confirm-modal context.
  // confirm modal 이 DOM 단일 (data-cart-confirm-modal 1개) 라 singleton 안전.
  let _cartConfirm = null;

  const Cascade = {

    // POS 패턴: 부모(anchor) 라인 삭제 시 linked gift 까지 cascade 삭제 + confirm.
    // 출처: misto-pos `useCart.ts#handleDeleteProduct` (lines 388-412).
    //
    // 🔴 cascade 처리 (2026-05-26): 부모 + 자식 모두 한 번의 /cart/update.js updates 로 제거.
    //   Shopify native remove (X 버튼 link 클릭) 은 부모 라인만 제거 → 자식 GY cart 라인 orphan 남음.
    //   우리가 e.preventDefault() 로 native 차단 후 다 처리.
    handleParentRemove(e, removeBtn, hub) {
      const cartItem = removeBtn.closest('.cart-item');
      if (!cartItem) return;
      // gift row 자체에는 remove 버튼 없으니 여기까지 올 일 없지만 방어적으로 차단
      if (cartItem.classList.contains('cart-item--gift')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const promoCell = cartItem.querySelector('[data-cart-promo-cell]');
      // 🔴 (2026-05-28) group_id 우선 매칭 — line-item.liquid 의 data-gift-parent 가 group_id 우선이므로 selector 매칭 정합.
      //   드로어 fallback: 드로어 line-item 엔 promo-cell 미렌더 (cart_item != true 분기). cartItem 의 data-gift-parent 사용.
      const groupId = promoCell?.dataset.appliedPromotionGroupId || '';
      const promoId = promoCell?.dataset.appliedPromotionId || '';
      const matchKey = groupId || cartItem.dataset.giftParent || promoId || '';
      if (!matchKey) return; // 식별 불가 — 일반 흐름 통과

      // 이 부모에 묶인 gift 자식이 있나? (data-gift-parent = group_id 또는 promo_id legacy)
      const giftChildren = document.querySelectorAll(
        `.cart-item--gift[data-gift-parent="${CSS.escape(matchKey)}"]`
      );
      if (giftChildren.length === 0) return; // gift 없음 — 일반 흐름 통과

      // 부모 + 자식 모두 native flow 차단 — modal confirm 후 우리가 한 번에 cart update.
      // 🔴 cart-remove-button (cart.js) 가 element 자체에 click listener 박아서 target phase 에서 즉시 제거됨.
      //    bindDelegations 의 click listener 는 capture phase 등록 → 우리가 먼저 잡고 stopPropagation 으로 차단.
      e.preventDefault();
      e.stopPropagation();

      // updates 미리 수집 (modal 띄우는 동안 DOM 바뀔 수 있어 즉시 snapshot).
      const updates = {};
      const parentLineKey = cartItem.dataset.lineKey || promoCell?.dataset.lineKey;
      if (parentLineKey) updates[parentLineKey] = 0;
      giftChildren.forEach((child) => {
        const childKey = child.dataset.lineKey || child.querySelector('[data-cart-promo-cell]')?.dataset.lineKey;
        if (childKey) updates[childKey] = 0;
      });
      if (!Object.keys(updates).length) return;

      // cart-confirm-modal 패턴 — clear-all / remove-bxgy-promo 와 같은 흐름.
      Cascade.openConfirm({
        action: 'remove-parent-cascade',
        title: 'Remove this item?',
        body: 'Linked promotion items will also be removed from your cart.',
        okLabel: 'Remove',
        context: { updates },
      });
    },

    // 🔴 부모 cascade — cart-confirm-modal OK 콜백.
    // 카트 페이지 / 드로어 양쪽에서 호출됨. drawer open 이면 cart-drawer-items 가 mount 되어
    // getSectionsToRender 가 cart-drawer 섹션까지 포함 → sections 응답으로 drawer DOM 도 갱신.
    async parentCascade({ updates } = {}, hub) {
      if (!updates || !Object.keys(updates).length) return;
      const drawerOpen = document.querySelector('cart-drawer')?.classList.contains('is-visible');
      const cartItemsEl = document.querySelector('cart-items');
      const drawerItemsEl = document.querySelector('cart-drawer-items');
      cartItemsEl?.classList.add('is-cart-loading');
      drawerItemsEl?.classList.add('is-loading');
      try {
        const sectionsHost = drawerItemsEl || cartItemsEl;
        const sectionsToRender = sectionsHost?.getSectionsToRender?.() || [];
        const sectionNames = sectionsToRender.map((s) => s.section).filter(Boolean);
        const body = sectionNames.length
          ? JSON.stringify({ updates, sections: sectionNames, sections_url: window.location.pathname })
          : JSON.stringify({ updates });
        const res = await fetch(MISTO.localeUrl('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body,
        });
        const data = await res.json().catch(() => null);

        if (data?.sections && sectionsToRender.length) {
          // 🔴 sections 기반 DOM swap (drawer + main-cart 한 번에 갱신).
          //    cart.js#updateQuantity 패턴 그대로. selector 우선 (좁힌 영역), id (wrapper) 는 fallback.
          sectionsToRender.forEach((section) => {
            const html = data.sections[section.section];
            if (!html) return;
            const target = document.querySelector(section.selector) || document.querySelector(section.id);
            if (!target) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
            if (parsed) target.innerHTML = parsed.innerHTML;
          });
          if (typeof updateCartCounters === 'function') updateCartCounters();
          if (typeof updateFreeShipping === 'function') updateFreeShipping();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } else if (!drawerOpen) {
          // drawer 미오픈 (카트 페이지) 면 기존 refreshCartSection — BXGY anchor 정렬 + cache buster 등 특수 처리 유지.
          await hub?.refreshCartSection();
        }
      } catch (err) {
        console.warn('[cart-promo-cascade] parentCascade', err);
      } finally {
        requestAnimationFrame(() => {
          document.querySelector('cart-items')?.classList.remove('is-cart-loading');
          document.querySelector('cart-drawer-items')?.classList.remove('is-loading');
        });
      }
    },

    openConfirm({ action, title, body, okLabel, context } = {}) {
      const modal = document.querySelector('[data-cart-confirm-modal]');
      if (!modal) return;
      _cartConfirm = { action, context };
      if (title) modal.querySelector('[data-cart-confirm-title]').textContent = title;
      if (body) modal.querySelector('[data-cart-confirm-body]').textContent = body;
      if (okLabel) {
        const okBtn = modal.querySelector('[data-cart-confirm-ok]');
        const span = okBtn?.querySelector('.button__text') || okBtn;
        if (span) span.textContent = okLabel;
      }
      modal.hidden = false;
      const scrollContainer = modal.querySelector('.cart-bxgy-modal__inner') || modal;
      if (typeof bodyScroll !== 'undefined') bodyScroll.lock(scrollContainer);
    },

    // 🔴 cancel 시 dropdown 복원 — BXGY 해제 confirm 에서 사용자가 X 누르면 select 값을 원래 promo 로 되돌림.
    // skipCancel 은 ok 클릭 흐름에서 true (이미 액션 진행 중이라 dropdown 되돌리면 안 됨).
    closeConfirm({ skipCancel } = {}, hub) {
      const modal = document.querySelector('[data-cart-confirm-modal]');
      if (!modal) return;
      const state = _cartConfirm;
      modal.hidden = true;
      const scrollContainer = modal.querySelector('.cart-bxgy-modal__inner') || modal;
      if (typeof bodyScroll !== 'undefined') bodyScroll.unlock(scrollContainer);

      if (!skipCancel && state?.action === 'remove-bxgy-promo' && state.context?.select) {
        state.context.select.value = state.context.prevValue || '';
        hub?.refreshDropdownInput?.(state.context.select);
      }
      _cartConfirm = null;
    },

    async handleConfirmOk(hub) {
      const action = _cartConfirm?.action;
      const context = _cartConfirm?.context;
      Cascade.closeConfirm({ skipCancel: true }, hub);
      if (action === 'clear-all') await Cascade.clearAllCart(hub);
      else if (action === 'remove-bxgy-promo') await Cascade.removeBxgyPromo(context, hub);
      else if (action === 'remove-parent-cascade') await Cascade.parentCascade(context, hub);
    },

    // 🔴 BXGY 해제 처리 — confirm OK 후 호출.
    //   (2026-05-28) 강화: 자식 cleanup (cart.js SSOT) + anchor 는 closure cell 직접 wipe (querySelector 의존 X).
    //   회귀 case: anchor querySelector 가 child 삭제 후 DOM mid-flight 으로 매칭 실패 → properties 미wipe.
    //   group_id 우선 매칭 → 자식 라인 cart 에서 삭제 + anchor properties wipe + 정가 복귀.
    async removeBxgyPromo({ cell, prevAppliedId } = {}, hub) {
      if (!cell || !prevAppliedId) return;
      const groupId = cell.dataset.appliedPromotionGroupId || '';
      const matchKey = groupId || prevAppliedId;
      const cartItemsEl = document.querySelector('cart-items');
      cartItemsEl?.classList.add('is-cart-loading');
      try {
        // 1) 자식 라인 삭제만 (anchor 는 closure cell 로 별도 처리)
        await window.CartPromoEffects.cleanupBxgyGroup(matchKey, { includeAnchor: false });
        // 2) anchor dataset + properties 직접 wipe — closure cell 사용 (querySelector 우회)
        cell.dataset.appliedPromotionId = '';
        cell.dataset.appliedPromotionType = '';
        cell.dataset.appliedPromotionRole = '';
        cell.dataset.appliedPromotionGroupId = '';
        delete cell.dataset.appliedPromotionAnchor;
        delete cell.dataset.appliedPromotionAddedByBxgy;
        await window.CartPromoEffects.clearLinePromotion(cell);
        // 3) cart UI 갱신
        await hub?.refreshCartSection();
      } finally {
        requestAnimationFrame(() => {
          document.querySelector('cart-items')?.classList.remove('is-cart-loading');
        });
      }
    },

    async clearAllCart(hub) {
      hub?.classList?.add?.('is-loading');
      try {
        await fetch(MISTO.localeUrl('/cart/clear.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
        });
        await hub?.refreshCartSection();
      } catch (err) {
        console.warn('[cart-promo-cascade] clearAllCart', err);
      } finally {
        hub?.classList?.remove?.('is-loading');
      }
    },
  };

  global.CartPromoCascade = Cascade;
})(typeof window !== 'undefined' ? window : globalThis);
